import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useForm from '../../../hooks/useForm';
import {
  JourneyChangeNoteType,
  JourneyChangePayload,
  JourneyChangeResponse,
  JourneyInfo,
} from '../../../types/journey';
import getMaterialInputsFormHelper from '../../../utils/getMaterialInputsFormHelper';
import { JourneyReliefEventType } from '../../../types/core';
import FormLayout, {
  FormLayoutInputElement,
} from '../../../components/FormLayout';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const reasonTranslationKeyMap: Record<
  JourneyChangeNoteType.Cancel | JourneyChangeNoteType.ReliefEvent,
  string
> = {
  [JourneyChangeNoteType.ReliefEvent]: 'relief-event',
  [JourneyChangeNoteType.Cancel]: 'other',
};

const JourneyCancellationDialog = ({
  journey,
  journeyId,
  onClose,
  onConfirm,
}: {
  journey?: JourneyInfo | null;
  journeyId: string | null;
  onClose: () => void;
  onConfirm: (newJourney: JourneyChangeResponse) => void;
}) => {
  const { cancelJourney } = useIotsApi();
  const cancelMutation = useMutation({
    mutationFn: (journeychangePayload: JourneyChangePayload) =>
      cancelJourney(journeychangePayload),
  });

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  const formState = useForm({
    initialValues: {
      reason: null,
      note: '',
      reliefEventType: JourneyReliefEventType.Weather,
      confirmCheckbox: false,
    },
    onSubmit: formValues => {
      if (journey) {
        cancelMutation.mutate(
          {
            journeyId: journeyId || null,
            journey: {
              operatorId: journey.operatorId,
              contractId: journey.contractId || null,
              routeId: journey.routeId || null,
              serviceType: journey.serviceType,
              arrivalPortId: journey.arrivalPortId,
              departurePortId: journey.departurePortId,
              day: journey.day,
              time: journey.time,
            },
            note: {
              note: formValues.note,
              type: formValues.reason || JourneyChangeNoteType.Cancel,
              reliefEventType:
                formValues.reason === JourneyChangeNoteType.ReliefEvent
                  ? formValues.reliefEventType
                  : null,
            },
          },
          {
            onSuccess: newJourney => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.journey-cancellation-success'),
              });
              onConfirm(newJourney);
              formState.reset();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.journey-cancellation-error'),
                ),
              });
              cancelMutation.reset();
            },
          },
        );
      }
    },
    validate: (
      { reason, note, confirmCheckbox, reliefEventType },
      setError,
    ) => {
      if (!reason) {
        setError('reason', t('validation.required-field'));
      }
      if (
        !note.trim() &&
        (reason === JourneyChangeNoteType.Cancel ||
          (reason === JourneyChangeNoteType.ReliefEvent &&
            reliefEventType === JourneyReliefEventType.Other))
      ) {
        setError('note', t('validation.required-field'));
      }
      if (!confirmCheckbox) {
        setError('confirmCheckbox', t('validation.required-checkbox'));
      }
    },
  });

  const {
    getSelectProps,
    getCheckboxProps,
    getBaseProps,
    getTextFieldProps,
    renderError,
  } = getMaterialInputsFormHelper(formState);

  const handleClose = () => {
    if (cancelMutation.isPending) {
      return;
    }
    onClose();
    formState.reset();
  };

  return (
    <ConfirmationDialog
      open={Boolean(journey)}
      loading={cancelMutation.isPending}
      onClose={handleClose}
      onConfirm={formState.handleSubmit}
      submitDisabled={formState.hasVisibleErrors}
      title={t('content.cancel-journey')}
    >
      <p className="mb-4 text-gray-500">
        {t(`content.cancel-journey-reason-note`)}
      </p>
      <FormLayout>
        <FormLayoutInputElement required label={t('content.reason')}>
          <FormControl error={getBaseProps('reason').error}>
            <Select
              {...getSelectProps('reason')}
              displayEmpty
              renderValue={(
                selected:
                  | JourneyChangeNoteType.Cancel
                  | JourneyChangeNoteType.ReliefEvent,
              ) => {
                if (!selected) {
                  return (
                    <span className="text-iots-grey-400">
                      {t('content.select')}
                    </span>
                  );
                }
                return t(`content.${reasonTranslationKeyMap[selected]}`);
              }}
            >
              <MenuItem value={JourneyChangeNoteType.ReliefEvent}>
                {t(`content.relief-event`)}
              </MenuItem>
              <MenuItem value={JourneyChangeNoteType.Cancel}>
                {t(`content.other`)}
              </MenuItem>
            </Select>
            {renderError('reason')}
          </FormControl>
        </FormLayoutInputElement>
        {formState.values.reason === JourneyChangeNoteType.ReliefEvent && (
          <FormLayoutInputElement required label={t('content.type')}>
            <FormControl error={getBaseProps('reliefEventType').error}>
              <Select {...getSelectProps('reliefEventType')}>
                {[
                  JourneyReliefEventType.Weather,
                  JourneyReliefEventType.Tide,
                  JourneyReliefEventType.Swell,
                  JourneyReliefEventType.Mechanical,
                  JourneyReliefEventType.Other,
                ].map(reliefEventType => (
                  <MenuItem key={reliefEventType} value={reliefEventType}>
                    {t(`journey-relief-event-type.${reliefEventType}`)}
                  </MenuItem>
                ))}
              </Select>
              {renderError('reason')}
            </FormControl>
          </FormLayoutInputElement>
        )}
        <FormLayoutInputElement
          required={
            formState.values.reason === JourneyChangeNoteType.Cancel ||
            (formState.values.reason === JourneyChangeNoteType.ReliefEvent &&
              formState.values.reliefEventType === JourneyReliefEventType.Other)
          }
          label={t('content.notes')}
        >
          <TextField
            {...getTextFieldProps('note', true)}
            placeholder={`${t('content.start-typing-here')}...`}
            multiline
            rows={5}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement>
          <FormControl
            error={getBaseProps('confirmCheckbox').error}
            component="fieldset"
            variant="standard"
          >
            <FormControlLabel
              control={<Checkbox {...getCheckboxProps('confirmCheckbox')} />}
              sx={{
                color: getBaseProps('confirmCheckbox').error
                  ? 'error.main'
                  : undefined,
                '& .MuiFormControlLabel-label': {
                  fontSize: {
                    xs: '0.875rem',
                    md: '1rem',
                  },
                },
              }}
              label={t(
                'content.confirmation-checkbox-publicly-visible-disclaimer',
              )}
            />
            {renderError('confirmCheckbox')}
          </FormControl>
        </FormLayoutInputElement>
      </FormLayout>
    </ConfirmationDialog>
  );
};

export default JourneyCancellationDialog;
